<template>
  <div :class="{ 'd-none': hide}">
      <div class="card-body shadow rounded">
          <div class="d-flex justify-content-between align-items-center m-2">
              <p class="d-inline-flex mb-0 mt-2">
                  <a data-bs-toggle="collapse" href="#mindmapTab" role="button" aria-expanded="true" aria-controls="mindmapTab" @click="this.collapsed = !this.collapsed">
                      <svg class="icon icon-primary fixed-icon">
                        <use :href='this.$root.baseURL + "/bootstrap-italia/dist/svg/sprites.svg#" + (this.collapsed ? "it-expand" : "it-collapse")'></use>
                      </svg>
                      Mindmap
                  </a>
              </p>
          </div>
          <hr class="my-0">
          <div class="p-2 show" id="mindmapTab">
              <div class="shadow m-0 p-0">
                  <div ref="container" class="markmap-container" />
              </div>
          </div>
      </div>
  </div>
</template>

<script>
import { ref, onMounted, watch } from 'vue'
import { Transformer } from 'markmap-lib'
import { Markmap, deriveOptions } from 'markmap-view'
import { Toolbar } from 'markmap-toolbar';
import * as d3 from 'd3'

export default {
  name: "MindMap",
  data() {
    return {
      collapsed: false,
    }
  },
  props: {
    mindmap: {
      type: Array,
      required: true,
      default: () => []
    }
  },
  setup(props) {
    const container = ref(null)
    const svgRef = ref(null) // per tenere traccia dello <svg>
    const hide = ref(true)
    const transformer = new Transformer()

    // Funzione per renderizzare la mappa mentale
    const renderMindmap = () => {
      if (!container.value || !props.mindmap || props.mindmap.length === 0) return;
      hide.value = false;

      // Rimuovi il vecchio SVG (se esiste)
      d3.select(container.value).selectAll('svg').remove();

      const { root } = transformer.transform(props.mindmap[0]);
      const svg = d3.select(container.value).append('svg')
        .attr('width', '100%')
        .attr('height', '100%')
        .attr('preserveAspectRatio', 'xMidYMid meet');

      svgRef.value = svg;
      const mm = Markmap.create(svg.node(), deriveOptions({ 
        autoFit: true, 
        maxWidth: 300, 
        initialExpandLevel: -1, 
        spacingVertical: 15, 
        colorFreezeLevel: 2 
      }), root);
      
      // Ottieni il baseURL dal template
      const baseURL = document.querySelector('meta[name="base-url"]')?.getAttribute('content') || '';
      
      // Crea e aggiungi la toolbar personalizzata
      createToolbar(mm, container.value, baseURL);
    };
    
    // Funzione per creare e personalizzare la toolbar
    const createToolbar = (mm, containerEl, baseURL) => {
      const { el } = Toolbar.create(mm);
      
      // Personalizza lo stile della toolbar per integrarla meglio con l'applicazione
      el.style.position = 'absolute';
      el.style.bottom = '1rem';
      el.style.right = '1rem';
      el.style.backgroundColor = 'rgba(255, 255, 255, 0.9)';
      el.style.borderRadius = '8px';
      el.style.padding = '5px';
      el.style.boxShadow = '0 2px 5px rgba(0, 0, 0, 0.2)';
      el.style.zIndex = '1000';
      el.style.transition = 'opacity 0.3s ease';
      
      // Aggiungi una classe per lo stile hover
      el.classList.add('markmap-toolbar-custom');
      
      // Rimuovi il logo dalla toolbar
      const logoButton = el.querySelector('.mm-toolbar-brand');
      if (logoButton) {
        logoButton.remove();
      }
      
      // Rimuovi il pulsante Toggle recursively che non fa nulla di utile
      const toggleRecursivelyButton = el.querySelector('[title="Toggle recursively"]');
      if (toggleRecursivelyButton) {
        toggleRecursivelyButton.remove();
      }
      
      // Crea un pulsante per il fullscreen
      const fullscreenButton = document.createElement('button');
      fullscreenButton.className = 'mm-toolbar-item';
      fullscreenButton.title = 'Fullscreen';
      fullscreenButton.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 24 24" class="icon icon-sm">
          <path d="M9,3H5C3.9,3,3,3.9,3,5v4c0,0.55,0.45,1,1,1s1-0.45,1-1V5h4c0.55,0,1-0.45,1-1S9.55,3,9,3z M19,3h-4c-0.55,0-1,0.45-1,1 s0.45,1,1,1h4v4c0,0.55,0.45,1,1,1s1-0.45,1-1V5C21,3.9,20.1,3,19,3z M5,15H4c-0.55,0-1,0.45-1,1v4c0,1.1,0.9,2,2,2h4 c0.55,0,1-0.45,1-1s-0.45-1-1-1H5v-4C5,15.45,4.55,15,5,15z M19,15c-0.55,0-1,0.45-1,1v4h-4c-0.55,0-1,0.45-1,1s0.45,1,1,1h4 c1.1,0,2-0.9,2-2v-4C20,15.45,19.55,15,19,15z"/>
        </svg>
      `;
      
      // Aggiungi l'evento per attivare il fullscreen
      fullscreenButton.addEventListener('click', () => {
        const container = containerEl;
        if (container) {
          // Alterna la classe fullscreen-mindmap
          if (container.classList.contains('fullscreen-mindmap')) {
            container.classList.remove('fullscreen-mindmap');
          } else {
            container.classList.add('fullscreen-mindmap');
            
            // Adatta la mappa alla nuova dimensione
            setTimeout(() => {
              if (mm.fit) mm.fit();
            }, 100);
          }
        }
      });
      
      // Aggiungi il pulsante alla toolbar
      el.appendChild(fullscreenButton);
      
      // Sostituisci completamente tutte le icone nella toolbar con SVG inline
      setTimeout(() => {
        // Usa setTimeout per assicurarsi che tutti gli elementi siano stati renderizzati
        const buttons = el.querySelectorAll('button, .mm-toolbar-item');
        buttons.forEach(button => {
          // Ottieni il titolo del pulsante o dell'elemento
          let title = button.getAttribute('title') || '';
          title = title.toLowerCase();
          
          // Salta il pulsante fullscreen che abbiamo già personalizzato
          if (title.includes('fullscreen')) {
            return;
          }
          
          // Mappa i titoli alle icone SVG inline
          let svgContent = '';
          
          if (title.includes('zoom in')) {
            svgContent = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 24 24" class="icon icon-sm">
              <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
            </svg>`;
          } else if (title.includes('zoom out')) {
            svgContent = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 24 24" class="icon icon-sm">
              <path d="M19 13H5v-2h14v2z"/>
            </svg>`;
          } else if (title.includes('fit')) {
            svgContent = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 24 24" class="icon icon-sm">
              <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/>
            </svg>`;
          } else if (title.includes('toggle')) {
            svgContent = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 24 24" class="icon icon-sm">
              <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"/>
            </svg>`;
          }
          
          // Se abbiamo trovato un'icona corrispondente, sostituisci
          if (svgContent) {
            // Rimuovi qualsiasi contenuto esistente
            button.innerHTML = svgContent;
          }
        });
      }, 100);
      
      // Applica stili comuni a tutti i pulsanti
      const allButtons = el.querySelectorAll('button, .mm-toolbar-item');
      allButtons.forEach(button => {
        button.style.margin = '0 2px';
        button.style.borderRadius = '4px';
        button.style.border = '1px solid #e0e0e0';
        button.style.padding = '4px';
        button.style.backgroundColor = '#ffffff';
        button.style.cursor = 'pointer';
        button.style.transition = 'all 0.2s ease';
        
        // Aggiungi effetto hover
        button.addEventListener('mouseover', () => {
          button.style.backgroundColor = '#f0f0f0';
          button.style.transform = 'translateY(-1px)';
        });
        
        button.addEventListener('mouseout', () => {
          button.style.backgroundColor = '#ffffff';
          button.style.transform = 'translateY(0)';
        });
      });
      
      // Aggiungi la toolbar al container
      container.value.appendChild(el);
      
      // Aggiungi uno stile per nascondere la toolbar quando non è in hover
      const toolbarContainer = container.value;
      toolbarContainer.style.position = 'relative';
      
      // Nascondi la toolbar quando il mouse non è sopra il container
      el.style.opacity = '0.4';
      
      // Rimuovi eventuali listener precedenti per evitare duplicati
      const newMouseOver = () => { el.style.opacity = '1'; };
      const newMouseOut = () => { el.style.opacity = '0.4'; };
      
      // Memorizza i listener per poterli rimuovere in seguito
      toolbarContainer._mouseOverListener = newMouseOver;
      toolbarContainer._mouseOutListener = newMouseOut;
      
      // Aggiungi i nuovi listener
      toolbarContainer.addEventListener('mouseover', newMouseOver);
      toolbarContainer.addEventListener('mouseout', newMouseOut);
      
      // Migliora lo stile dei pulsanti nella toolbar
      const toolbarButtons = el.querySelectorAll('button');
      toolbarButtons.forEach(button => {
        button.style.margin = '0 2px';
        button.style.borderRadius = '4px';
        button.style.border = '1px solid #e0e0e0';
        button.style.padding = '4px';
        button.style.backgroundColor = '#ffffff';
        button.style.cursor = 'pointer';
        button.style.transition = 'all 0.2s ease';
        
        // Aggiungi effetto hover
        button.addEventListener('mouseover', () => {
          button.style.backgroundColor = '#f0f0f0';
          button.style.transform = 'translateY(-1px)';
        });
        
        button.addEventListener('mouseout', () => {
          button.style.backgroundColor = '#ffffff';
          button.style.transform = 'translateY(0)';
        });
      });
      
      return el;
    }

    onMounted(() => {
      renderMindmap()
    })

    watch(() => props.mindmap, () => {
      renderMindmap()
    }, { deep: true })



    return {
      container,
      hide,
      svgRef,
      transformer,
      createToolbar
    }
  }
}


</script>

<style scoped>
/* Stili per la mappa mentale */
.markmap-container {
    width: 100%;
    height: 500px;
    background-color: #ffffff;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: inset 0 0 10px rgba(0, 0, 0, 0.1);
    position: relative;
    /* Sfondo a puntini stile foglio grafico */
    background-image: radial-gradient(#e0e0e0 1px, transparent 1px);
    background-size: 20px 20px;
    background-position: -10px -10px;
    transition: height 0.3s ease;
}

.fullscreen-mindmap {
    position: fixed !important;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    z-index: 9999;
    background-color: white;
    padding: 20px;
}

/* Stili per la toolbar personalizzata */
.markmap-toolbar-custom button:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
}

/* Stile per i tooltip nella toolbar */
.markmap-toolbar-custom button[title]:hover::after {
    content: attr(title);
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    background-color: rgba(0, 0, 0, 0.8);
    color: white;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 12px;
    white-space: nowrap;
    pointer-events: none;
    margin-bottom: 5px;
    z-index: 1001;
}

.fullscreen-mindmap .card-body {
    flex: 1;
    display: flex;
    flex-direction: column;
    max-height: 100%;
}

.fullscreen-mindmap #mindmapTab {
    flex: 1;
    display: flex;
    flex-direction: column;
}

.fullscreen-mindmap #mindmapTab > div {
    flex: 1;
}

.fullscreen-mindmap .markmap-container {
    height: calc(100vh - 113px);
}

svg:not(.fixed-icon) {
    width: 100% !important;
    height: 100% !important;
    display: block;
}

.fixed-icon {
    min-width: 24px !important;
    min-height: 24px !important;
}
</style>
